import React, { useMemo } from "react";

import { Checkbox, Stack } from "@mui/material";
import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { currencySign } from "constants/index";
import { useActionHook, useFormatDateHook, useGetGroupNames, usePrompt } from "hooks";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import { useDealActions } from "store";
import { CurrencyType, IBulkFileRow, IFileRepo } from "store/types";
import { formatCurrency } from "utils/strings";
import toIsoMidday from "utils/to-iso-midday";
import { toastify } from "utils/tyostify";

import { ClassificationPrompt } from "../../../File-repository/Table/ClassificationPrompt";
import { CurrencyPrompt } from "../../../File-repository/Table/CurrencyPrompt";
import { EffectiveDatePrompt } from "../../../File-repository/Table/EffectiveDatePrompt";
import { InvestorPrompt } from "../../../File-repository/Table/InvestorPrompt";
import { AddValueButton, EditableCell, FileNameCell, TextCell, tooltipHeader } from "../components";
import { bulkTableBaseOptions } from "./TableOptions";

interface Props {
  rows: IBulkFileRow[];
  measureColumns: string[];
  readOnly: boolean;
  onUpdateFile: (fileId: number, patch: Partial<IBulkFileRow>) => void;
  onRefetch: () => void;
  selectedIds: number[];
  onToggleSelect: (fileId: number) => void;
}

export const BulkReviewWideTable: React.FC<Props> = ({
  rows,
  measureColumns,
  readOnly,
  onUpdateFile,
  onRefetch,
  selectedIds,
  onToggleSelect
}) => {
  const formatDate = useFormatDateHook();
  const openPrompt = usePrompt();
  const getGroupNames = useGetGroupNames();
  const { updateFileRepoAction } = useDealActions();

  const openFilePreview = async (fileId: number, fileName: string) => {
    await openPrompt({
      componentType: "fullscreen",
      Component: DataExtractionModal,
      props: {
        fileIds: [fileId],
        fileNames: { [fileId]: fileName },
        source: "deal" as const,
        initialShowParameters: true,
        hideDoneButton: true
      }
    });
    onRefetch();
  };

  const investorDisplay = (file: IFileRepo): string => {
    if (file.all_investors) return "All Investors";
    if (file.investor_ids?.length) {
      const groupNames = getGroupNames(file.investor_ids);
      if (groupNames) return groupNames;
    }
    return file.investor?.name ?? "—";
  };

  const { action: updateFile } = useActionHook(updateFileRepoAction, {
    onSuccess: (file) => {
      onUpdateFile(file.id, {
        documentType: file.document_type ?? "",
        currency: (file.currency ?? "") as CurrencyType,
        effectiveDate: file.effective_date ?? null,
        dealId: file.deal?.id ?? null,
        investorId: file.investor?.id ?? null,
        allInvestors: file.all_investors,
        investorIds: file.investor_ids ?? [],
        splitByCommitment: file.split_by_commitment,
        entireCommitment: file.entire_commitment ?? false,
        investorName: investorDisplay(file)
      });
      onRefetch();
    },
    onReject: (error) => toastify({ message: error?.message || "Failed to update the file.", type: "error" })
  });

  const columns = useMemo<MRT_ColumnDef<IBulkFileRow>[]>(() => {
    const editable = (value: string, isEmpty: boolean, onClick: (e: React.MouseEvent<HTMLDivElement>) => void) => {
      if (isEmpty) return readOnly ? <TextCell value="—" /> : <AddValueButton onClick={onClick} />;
      return <EditableCell value={value} onClick={onClick} disabled={readOnly} />;
    };

    const base: MRT_ColumnDef<IBulkFileRow>[] = [
      {
        id: "select",
        header: "",
        size: 43,
        minSize: 43,
        grow: false,
        enableSorting: false,
        enableColumnActions: false,
        enableResizing: false,
        Cell: ({ row }) => (
          <Checkbox
            size="small"
            disableRipple
            sx={{ "&:hover": { backgroundColor: "transparent" }, padding: 0 }}
            onChange={() => onToggleSelect(row.original.fileId)}
            checked={selectedIds.includes(row.original.fileId)}
          />
        )
      },
      {
        id: "fileName",
        accessorKey: "fileName",
        header: "File Name",
        size: 320,
        enableColumnActions: false,
        enableSorting: false,
        Header: tooltipHeader("File Name"),
        Cell: ({ row }) => (
          <FileNameCell
            fileType={row.original.fileType}
            fileName={row.original.fileName}
            onClick={() => openFilePreview(row.original.fileId, row.original.fileName)}
          />
        )
      },
      {
        id: "documentType",
        accessorKey: "documentType",
        header: "Altius Classification",
        size: 220,
        enableColumnActions: false,
        enableSorting: false,
        Header: tooltipHeader("Altius Classification"),
        Cell: ({ row }) => {
          const onClick = async (e: React.MouseEvent<HTMLDivElement>) => {
            const result = await openPrompt({
              componentType: "popover",
              anchorEl: e.currentTarget as HTMLElement,
              Component: ClassificationPrompt,
              props: { currentClassification: row.original.documentType || null }
            });
            if (!result) return;
            updateFile({ fileId: row.original.fileId, document_type: result.classification });
          };
          return editable(row.original.documentType, !row.original.documentType, onClick);
        }
      },
      {
        id: "investorName",
        accessorKey: "investorName",
        header: "Investor",
        enableColumnActions: false,
        enableSorting: false,
        Header: tooltipHeader("Investor"),
        Cell: ({ row }) => {
          const { investorId, allInvestors, investorIds } = row.original;
          const onClick = async (e: React.MouseEvent<HTMLDivElement>) => {
            const result = await openPrompt({
              componentType: "popover",
              anchorEl: e.currentTarget as HTMLElement,
              Component: InvestorPrompt,
              props: {
                currentInvestorId: investorId,
                currentAllInvestors: allInvestors,
                currentSplitByCommitment: row.original.splitByCommitment,
                currentInvestorIds: investorIds,
                currentEntireCommitment: row.original.entireCommitment,
                dealId: row.original.dealId
              }
            });
            if (!result) return;
            const isGroups = Boolean(result.investorIds.length);
            updateFile({
              fileId: row.original.fileId,
              investor_id: result.investor?.id ?? null,
              all_investors: result.allInvestors,
              split_by_commitment: result.splitByCommitment,
              investor_ids: result.investorIds,
              entire_commitment: isGroups ? result.entireCommitment : false,
              group_commitment: isGroups ? !result.entireCommitment : false
            });
          };
          const isEmpty = !investorId && !allInvestors && !investorIds.length;
          return editable(row.original.investorName, isEmpty, onClick);
        }
      },
      {
        id: "currency",
        accessorKey: "currency",
        header: "Currency",
        size: 130,
        enableColumnActions: false,
        enableSorting: false,
        Header: tooltipHeader("Currency"),
        Cell: ({ row }) => {
          const onClick = async (e: React.MouseEvent<HTMLDivElement>) => {
            const result = await openPrompt({
              componentType: "popover",
              anchorEl: e.currentTarget as HTMLElement,
              Component: CurrencyPrompt,
              props: { currentCurrency: row.original.currency ?? null }
            });
            if (!result || !result.currency) return;
            updateFile({ fileId: row.original.fileId, currency: result.currency });
          };
          return editable(String(row.original.currency ?? ""), !row.original.currency, onClick);
        }
      },
      {
        id: "effectiveDate",
        accessorKey: "effectiveDate",
        header: "Effective Date",
        enableColumnActions: false,
        enableSorting: false,
        Header: tooltipHeader("Effective Date"),
        Cell: ({ row }) => {
          const onClick = async (e: React.MouseEvent<HTMLDivElement>) => {
            const result = await openPrompt({
              componentType: "popover",
              anchorEl: e.currentTarget as HTMLElement,
              Component: EffectiveDatePrompt,
              props: { currentDate: row.original.effectiveDate ?? null }
            });
            if (!result) return;
            updateFile({
              fileId: row.original.fileId,
              effective_date: result.effectiveDate ? toIsoMidday(result.effectiveDate) : null
            });
          };
          const value = row.original.effectiveDate
            ? formatDate({ date: row.original.effectiveDate, format: "DD/MM/YYYY" })
            : "";
          return editable(value, !row.original.effectiveDate, onClick);
        }
      }
    ];

    const measures: MRT_ColumnDef<IBulkFileRow>[] = measureColumns.map((name) => ({
      id: `measure_${name}`,
      header: name,
      size: 160,
      enableColumnActions: false,
      enableSorting: false,
      Header: tooltipHeader(name),
      Cell: ({ row }) => {
        const cell = row.original.measures[name];
        if (!cell || cell.value == null) return <TextCell value="—" />;
        const formatted = formatCurrency({ value: cell.value, currencySymbol: currencySign[cell.currency] ?? "" });
        const tooltip = cell.extraCount > 0 ? `${formatted} (+${cell.extraCount} more)` : formatted;
        return <TextCell value={formatted} tooltip={tooltip} />;
      }
    }));

    return [...base, ...measures];
  }, [measureColumns, formatDate, readOnly, openPrompt, updateFile, selectedIds, onToggleSelect]);

  const table = useMaterialReactTable<IBulkFileRow>({
    ...bulkTableBaseOptions<IBulkFileRow>(),
    defaultColumn: { grow: true, size: 160, minSize: 120 },
    columns,
    data: rows,
    getRowId: (row) => String(row.fileId)
  });

  return (
    <Stack sx={{ borderRadius: "8px", overflow: "hidden", flex: 1, minHeight: 0, minWidth: 0 }}>
      <MaterialReactTable table={table} />
    </Stack>
  );
};
